import { Injectable, inject } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';

declare global {
  interface Window {
    _paq?: Array<Array<string | number | boolean>>;
  }
}

@Injectable({
  providedIn: 'root',
})
export class MatomoService {
  private readonly router = inject(Router);

  /**
   * Set analyst ID and firm ID as Matomo custom dimensions
   * dimension1 = Analyst ID
   * dimension2 = Firm ID
   * @param analystId - The analyst ID to track
   * @param firmId - The firm ID to track
   */
  setUserDimensions(analystId?: number, firmId?: number): void {
    // Ensure queue exists
    window._paq = window._paq || [];

    if (analystId) {
      // Set custom dimension 1 (Analyst ID)
      window._paq.push(['setCustomDimension', 1, analystId.toString()]);
    }

    if (firmId) {
      // Set custom dimension 2 (Firm ID)
      window._paq.push(['setCustomDimension', 2, firmId.toString()]);
    }
  }

  private initialized = false;

  /**
   * Initialize Matomo SPA tracking
   * Automatically tracks page views on route changes
   * Creates queue if Matomo hasn't loaded yet (for async loading)
   */
  initialize(): void {
    if (this.initialized) {
      return;
    }
    this.initialized = true;

    // Ensure queue exists for async Matomo loading
    // Events pushed before Matomo loads will be processed once it attaches
    window._paq = window._paq || [];

    // Track all page views via NavigationEnd events only.
    // The initial page load and any redirects (e.g. / → RoleRedirect → /vendors)
    // each emit NavigationEnd, so we rely solely on the router to avoid duplicates.
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.trackPageView(event.urlAfterRedirects);
      });
  }

  /**
   * Manually track a page view
   * @param url - Optional URL to track (defaults to current location)
   */
  private trackPageView(url?: string): void {
    // Ensure queue exists
    window._paq = window._paq || [];

    if (url) {
      window._paq.push(['setCustomUrl', url]);
    }

    window._paq.push(['trackPageView']);
  }

  /**
   * Track a custom event
   * @param category - Event category
   * @param action - Event action
   * @param name - Optional event name
   * @param value - Optional numeric value
   */
  trackEvent(
    category: string,
    action: string,
    name?: string,
    value?: number,
  ): void {
    // Ensure queue exists
    window._paq = window._paq || [];

    const eventData: Array<string | number> = ['trackEvent', category, action];
    if (name) eventData.push(name);
    if (value !== undefined) eventData.push(value);

    window._paq.push(eventData);
  }

  scanForMedia(): void {
    window._paq = window._paq || [];
    window._paq.push(['MediaAnalytics::scanForMedia']);
  }
}
